import { useContext } from 'react';
import isEmpty from 'lodash.isempty';
import {
  Button, ColumnLayout, Column, Form, FieldSet, Typography, MenuSurface, Avatar, Icon, ModalBody,
} from '@visionplanner/ui-react-material';
import { addEmployeeFormSchema } from './partner-form-validation';
import { StyledProfile } from '../partner-styles';
import { StyledModalFooter } from '../../styles';
import { PartnerContext } from '../partner-context';
import { addEmployee, deleteEmployee } from '../partner-onboarding.requests';
import { types } from '../partner-onboarding.reducer';
import { GENDER_OPTIONS } from '../../../common/enums';
import { useAsyncCallback } from '../../../common/custom-hooks/use-async-callback';
import { useNotifications } from '../../../common/notification-provider/notification-provider';
import { useModal } from '../../../common/modal-dialog/modal-dialog-container';

const ADD_EMPLOYEE_FORM_INIITAL_VALUES = Object.freeze({
  email: '',
  firstName: '',
  middleName: '',
  lastName: '',
  hasAdminRights: false,
  gender: '',
});

const HIDE_MODAL_ID = 'confirm';

/** Component to add and delete employee details */

const AddEmployees = () => {
  const {
    activeStep,
    setActiveStep,
    state,
    dispatch,
  } = useContext(PartnerContext);

  const { showNotification } = useNotifications();
  const { showModal, hideModal } = useModal();

  const { partnerId, employeeInformation } = state;
  const handleSubmit = useAsyncCallback(async (formValues, { resetForm, setSubmitting }) => {
    try {
      /** selectedTrainings is removed from the F.E but not from the B.E as
       * it is a required property will remove it after the release
       */
      await addEmployee(partnerId, { ...formValues, selectedTrainings: [] });
      dispatch({
        type: types.addEmplyoee,
        payload: [...employeeInformation, { ...formValues, selectedTrainings: [] }],
      });
      resetForm();
      showNotification({ title: 'Medewerker toegevoegd', type: 'success' });
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, employeeInformation, partnerId]);

  const handleRemoveEmployee = useAsyncCallback(async (email) => {
    await deleteEmployee(partnerId, email);
    dispatch({
      type: types.deleteEmployee,
      payload: employeeInformation.filter((employee) => employee.email !== email),
    });
    showNotification({ title: 'Medewerker verwijderd', type: 'success' });
  }, [dispatch, employeeInformation, partnerId]);

  const handleConfirmationModal = (isDirty) => {
    if (isDirty) {
      showModal('confirm', {
        title: 'Afsluiten zonder op te slaan?',
        children: 'De medewerker is nog niet toegevoegd. Weet je zeker dat je door wilt gaan?',
        handleConfirm: () => { hideModal(HIDE_MODAL_ID); setActiveStep(activeStep + 1); },
        dataTa: 'confirmation-modal',
        confirmText: 'Ja',
        cancelText: 'Nee',
      });
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  return (
    <>
      <Form
        initialValues={ADD_EMPLOYEE_FORM_INIITAL_VALUES}
        validateFormSchema={addEmployeeFormSchema}
        onSubmit={handleSubmit}
        dataTa="add-employee-form"
        isInitiallyValid={addEmployeeFormSchema.isValidSync(employeeInformation)}
        className="full-height flex-column"
      >
        {
          ({ isValid, isDirty }) => (
            <>
              <ModalBody>
                <Typography use="subtitle2" className="flex">Voeg de medewerkers die naast jou aan de training meedoen direct toe. Ben je de enige persoon die de training volgt, ga dan naar de volgende pagina.</Typography>
                <ColumnLayout>
                  <Column span={9}>
                    <FieldSet
                      type="email"
                      name="email"
                      required
                      label="E-mailadres"
                      dataTa="employee-email"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={4}>
                    <FieldSet
                      type="text"
                      name="firstName"
                      required
                      label="Voornaam"
                      dataTa="employee-first-name"
                    />
                  </Column>
                  <Column span={4}>
                    <FieldSet
                      type="text"
                      name="middleName"
                      label="Tussenvoegsel"
                      dataTa="employee-middle-name"
                    />
                  </Column>
                  <Column span={4}>
                    <FieldSet
                      type="text"
                      name="lastName"
                      required
                      label="Achternaam"
                      dataTa="employee-last-name"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={3}>
                    <FieldSet
                      type="selectOne"
                      controlType="autocomplete"
                      label="Geslacht"
                      name="gender"
                      options={GENDER_OPTIONS}
                      placeholder="Selecteer"
                      required
                      dataTa="type-of-gender"
                      noOptionsMessage="Geen opties"
                      className="type-of-gender"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={12}>
                    <FieldSet
                      type="boolean"
                      controlType="checkbox"
                      name="hasAdminRights"
                      placeholder="Deze medewerker is beheerder"
                      className="employee-rights"
                      disabled={!isValid}
                      dataTa="has-admin-rights"
                    />
                  </Column>
                </ColumnLayout>
                <Button className="mar-t-sm" dataTa="add-employee" type="submit" disabled={!isValid}>Medewerker toevoegen</Button>
                { !isEmpty(state.employeeInformation)
                && (
                  <StyledProfile className="mar-ver-md flex pad-ver-sm">
                    { state.employeeInformation.map((employee, key) => (
                      <MenuSurface
                        handle={<Avatar className="employee-profile" dataTa="employee-avatar" name={`${employee.firstName} ${employee.lastName.replace(/\s/g, '')}`} />}
                        position="topRight"
                        key={key}
                      >
                        <div className="pad-sm">
                          <Typography className="flex" use="normal-text" dataTa="employee-name">
                            {`${employee.firstName} ${employee.middleName} ${employee.lastName}`}
                          </Typography>
                          <Typography use="normal-text" dataTa="employee-email">
                            {employee.email}
                          </Typography>
                          <Icon
                            iconSet="fas"
                            size="xsmall"
                            name="trash"
                            onClick={() => handleRemoveEmployee(employee.email)}
                            dataTa="delete-icon"
                          />
                        </div>
                      </MenuSurface>
                    ))}
                  </StyledProfile>
                )}
              </ModalBody>
              <StyledModalFooter>
                <Button buttonType="secondary" dataTa="go-to-firm-address" onClick={() => setActiveStep(activeStep - 1)}>Vorige</Button>
                <Button dataTa="go-to-other-softwares-used" onClick={() => handleConfirmationModal(isDirty)}>Volgende</Button>
              </StyledModalFooter>
            </>
          )
        }
      </Form>
    </>
  );
};

export default AddEmployees;
